import React from 'react';

import { AATeamMembers } from '@components/AccountTeamsApp/components/icons';
import { Alert, SelectOption } from '@components/common';
import { BankSVG, InfoCircleIcon, NoCircleSVG } from '@components/svgs';
import { Tooltip } from '@components/Tooltip';
import { useFeature } from '@hooks/useFeature';

export const contactAccessOrAffiliation = (hasTeamAccessControl: boolean): string =>
  hasTeamAccessControl ? 'Affiliation' : 'Contact access';

export const anyoneOrWorkspace = (hasTeamAccessControl: boolean): string =>
  hasTeamAccessControl ? 'Workspace' : 'Anyone';

export const contactAccessOrAffiliationDescription = (hasTeamAccessControl: boolean): string =>
  hasTeamAccessControl
    ? 'Indicates the level of access different parts of the organization have to a candidate. It identifies whether the candidate is available to the entire workspace or restricted to specific teams, helping manage visibility and collaboration across departments.'
    : 'Which teams can contact this candidate';

export const contactAccessOrAffiliationSecondaryDescription = (hasTeamAccessControl: boolean): string =>
  hasTeamAccessControl
    ? 'Set whether the candidate is affiliated with the Workspace, specific Teams, or no one.'
    : 'This determines who will be able to contact the candidates.';

export const contactAccessLabels = (hasTeamAccessControl: boolean): Record<ContactAccessType | 'no_one', string> => ({
  public: hasTeamAccessControl ? 'Workspace' : 'Anyone on the workspace',
  needs_team: 'Only specific teams',
  no_one: 'No one'
});

export const contactAccessOptions = (hasTeamAccessControl: boolean): SelectOption<ContactAccessType | 'no_one'>[] => [
  {
    label: contactAccessLabels(hasTeamAccessControl).public,
    value: 'public',
    Icon: BankSVG
  },
  {
    label: contactAccessLabels(hasTeamAccessControl).needs_team,
    value: 'needs_team',
    Icon: AATeamMembers
  },
  {
    label: contactAccessLabels(hasTeamAccessControl).no_one,
    value: 'no_one',
    Icon: NoCircleSVG
  }
];

export const ContactAccessTooltip: React.FC<React.PropsWithChildren<any>> = (props) => {
  const hasTeamAccessControl = useFeature('team_access_control');

  return (
    <Tooltip content={contactAccessOrAffiliationDescription(hasTeamAccessControl)}>
      <InfoCircleIcon className='h-4 w-4 flex-shrink-0' />
    </Tooltip>
  );
};

export const NeedsToBePublicAlert: React.FC<{ studyTitle: string | undefined }> = ({ studyTitle }) => {
  const hasTeamAccessControl = useFeature('team_access_control');

  let heading = 'Contact access must be public';
  let description = `${studyTitle} doesn’t belong to any team, so contact access must be set to “Anyone on the workspace”.`;

  if (hasTeamAccessControl) {
    heading = 'Affiliation must be set to "Workspace"';
    description = `${studyTitle} doesn’t belong to any team, so affiliation must be set to “Workspace”.`;
  }

  return (
    <Alert type='error' heading={heading}>
      {description}
    </Alert>
  );
};

type Props = {
  studyTitle: string | undefined;
  studyTeamName: string | undefined;
};

export const MissingTeamAlert: React.FC<Props> = ({ studyTitle, studyTeamName }) => {
  const hasTeamAccessControl = useFeature('team_access_control');

  const heading = `${contactAccessOrAffiliation(hasTeamAccessControl)} must include ${studyTeamName}`;
  const description = `${studyTitle} belongs to ${studyTeamName}, so ${contactAccessOrAffiliation(hasTeamAccessControl).toLowerCase()} must include this team.`;

  return (
    <Alert type='error' heading={heading}>
      {description}
    </Alert>
  );
};
